import {
  Button,
  InnerContent,
  ModalHeader,
  SimpleModal,
  SimpleModalFooter,
} from '@fountain/fountain-ui-components';
import {
  CircularProgress,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { MergeKeysService, PaginateJsonSchema } from 'api-clients/monolith';
import React, { useCallback, useEffect, useState, VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Pagination } from 'components/Pagination';
import { addMessageAction } from 'containers/FlashMessage/actions';
import { useApiService } from 'hooks/useApiService';
import { useApiServiceMutation } from 'hooks/useApiServiceMutation';
import { useDebounce } from 'hooks/useDebounce';
import globalMessages from 'shared/global/messages';

import { MergeKeyObject } from '../type';
import { messages } from './messages';

// ********************************************************************************
// == Type ========================================================================
type Props = {
  accountExternalId: string;
  onClose: () => void;
  onCreated: () => void;
  open: boolean;
};

type AvailableMergeKeysResponse = {
  // eslint-disable-next-line camelcase
  merge_keys: string[];
  pagination: PaginateJsonSchema;
};

// == Constant ====================================================================
const DEFAULT_PER_PAGE = 25;
const DIALOG_TITLE_ID = 'add-merge-key';
const SEARCH_DEBOUNCE_MS = 300;
const MODAL_MAX_WIDTH = 1000;

const postService = (
  accountId: string,
  requestBody?: { key: string; translatable?: boolean },
) => MergeKeysService.postInternalApiMergeKeys(accountId, requestBody);

const useStyles = makeStyles(theme => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minHeight: 320,
  },
  emptyContainer: {
    alignItems: 'center',
    color: theme.palette.common.gray700,
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    padding: theme.spacing(4, 0),
    textAlign: 'center',
  },
  footerActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  list: {
    maxHeight: 320,
    overflowY: 'auto',
  },
  loadingContainer: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    padding: theme.spacing(4, 0),
  },
  row: {
    cursor: 'pointer',
  },
  keyText: {
    wordBreak: 'break-all',
  },
}));

// == Component ===================================================================
const AddMergeKeyDialog: VFC<Props> = ({
  open,
  accountExternalId,
  onClose,
  onCreated,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const intl = useIntl();

  // -- State ---------------------------------------------------------------------
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // -- Effect ---------------------------------------------------------------------
  useEffect(() => {
    // reset local state
    if (!open) {
      return;
    }

    setSearchInput('');
    setQuery('');
    setPage(1);
    setPerPage(DEFAULT_PER_PAGE);
    setSelectedKey(null);
  }, [open]);

  // -- Callback ------------------------------------------------------------------
  const fetchAvailableMergeKeys = useCallback(
    () =>
      MergeKeysService.getInternalApiMergeKeysAvailable(
        accountExternalId,
        query || undefined,
        page,
        perPage,
      ),
    [accountExternalId, query, page, perPage],
  );
  const { result } = useApiService<AvailableMergeKeysResponse>(
    fetchAvailableMergeKeys,
    {
      enabled: open,
    },
  );

  // -- Mutation ------------------------------------------------------------------
  const { mutation: createMergeKey, result: mutationResult } =
    useApiServiceMutation<MergeKeyObject, typeof postService>(postService, {
      onSuccess: () => {
        dispatch(
          addMessageAction(
            intl.formatMessage(messages.createSuccess),
            'success',
          ),
        );
        onCreated();
        onClose();
      },
      onError: () => {
        dispatch(
          addMessageAction(intl.formatMessage(messages.createError), 'error'),
        );
      },
    });

  // -- Handler -------------------------------------------------------------------
  const debouncedSetQuery = useDebounce((value: string) => {
    setQuery(value);
    setPage(1);
  }, SEARCH_DEBOUNCE_MS);

  const handleConfirm = () => {
    if (!selectedKey) {
      return;
    }

    createMergeKey(accountExternalId, {
      key: selectedKey,
      translatable: true,
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchInput(value);
    debouncedSetQuery(value);
  };

  // -- UI ------------------------------------------------------------------------
  if (!open) {
    return null;
  }
  const title = intl.formatMessage(globalMessages.add);
  const isMutating = mutationResult.isLoading;

  const renderBody = () => {
    if (result.status === 'loading') {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      );
    }

    if (result.status === 'error') {
      return (
        <Typography
          className={classes.emptyContainer}
          variant="body2"
          data-testid="add-merge-key-error"
        >
          <FormattedMessage {...messages.errorLoadingAvailable} />
        </Typography>
      );
    }

    const data =
      result.status === 'ready' || result.status === 'reloading'
        ? result.data
        : { merge_keys: [], pagination: undefined };
    const rows = data.merge_keys;
    const { pagination } = data;

    if (rows.length === 0) {
      const message = query ? messages.noMatches : messages.noKeysAvailable;
      return (
        <Typography
          className={classes.emptyContainer}
          variant="body2"
          data-testid="add-merge-key-empty"
        >
          <FormattedMessage {...message} values={{ query }} />
        </Typography>
      );
    } /* else -- there are options matching the query */

    return (
      <>
        <TableContainer className={classes.list} role="radiogroup">
          <Table size="small">
            <TableBody>
              {rows.map(key => (
                <TableRow
                  className={classes.row}
                  hover
                  key={key}
                  onClick={() => setSelectedKey(key)}
                  selected={selectedKey === key}
                >
                  <TableCell padding="checkbox">
                    <Radio
                      checked={selectedKey === key}
                      color="primary"
                      inputProps={{ 'aria-label': key }}
                      onChange={() => setSelectedKey(key)}
                      value={key}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography className={classes.keyText} variant="body2">
                      {key}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {pagination && pagination.total_pages > 1 && (
          <Pagination
            currentPage={pagination.current_page}
            lastPage={pagination.total_pages}
            perPage={pagination.per_page}
            setPage={setPage}
            setPerPage={setPerPage}
          />
        )}
      </>
    );
  };

  return (
    <SimpleModal
      ariaLabelledBy={DIALOG_TITLE_ID}
      blurOverlay
      fullScreenOnMobile={false}
      maxWidth={MODAL_MAX_WIDTH}
      onClose={onClose}
    >
      <ModalHeader
        ariaLabelledBy={DIALOG_TITLE_ID}
        onClose={onClose}
        showIcon={false}
      >
        {title}
      </ModalHeader>
      <InnerContent>
        <div className={classes.body}>
          <TextField
            fullWidth
            placeholder={intl.formatMessage(messages.searchPlaceholder)}
            value={searchInput}
            onChange={handleSearchChange}
            variant="outlined"
            size="small"
            inputProps={{
              'aria-label': intl.formatMessage(messages.searchPlaceholder),
            }}
          />
          {renderBody()}
        </div>
      </InnerContent>
      <SimpleModalFooter>
        <div className={classes.footerActions}>
          <Button
            autoWidth
            disabled={isMutating}
            onClick={onClose}
            size="small"
            type="secondary"
          >
            <FormattedMessage {...globalMessages.cancel} />
          </Button>
          <Button
            autoWidth
            disabled={!selectedKey || isMutating}
            isLoading={isMutating}
            onClick={handleConfirm}
            size="small"
            type="primary"
          >
            <FormattedMessage {...globalMessages.confirm} />
          </Button>
        </div>
      </SimpleModalFooter>
    </SimpleModal>
  );
};

// == Export ======================================================================
export default AddMergeKeyDialog;
